// Data-driven SEO landing pages. Each entry becomes a route and a sitemap URL.
// Keep slugs lowercase-kebab and unique. Update sitemap.xml when adding entries.

export type ProgramKey =
  | "after_school"
  | "early_care"
  | "clubs"
  | "summer_camps"
  | "holiday_camps";

export interface LandingPageConfig {
  slug: string; // path without leading slash
  h1: string;
  h1Accent?: string; // colored portion of the heading
  seoTitle: string;
  seoDescription: string;
  heroSubtitle: string;
  accent: "emerald" | "amber" | "violet" | "rose" | "sky" | "primary";
  programFilter?: ProgramKey; // links CTAs to /programs?category=...
  location?: {
    name: string;
    address: string;
    city: string;
    phone: string;
  };
  neighborhood?: string; // e.g. "North Phoenix", "Paradise Valley"
  keywords: string[]; // shown in body copy for topical relevance
  highlights: { title: string; description: string }[];
  faqs: { question: string; answer: string }[];
}

const FREEDOM = {
  name: "Freedom Academy",
  address: "3916 E Paradise Lane",
  city: "Phoenix, AZ 85032",
  phone: "602-550-0931",
};

const RIDGELINE = {
  name: "Ridgeline Academy",
  address: "33625 N Valley Parkway",
  city: "Phoenix, AZ 85085",
  phone: "602-550-0931",
};

const programBenefit = {
  after_school: [
    { title: "Homework Help", description: "Dedicated time and quiet space for daily assignments with staff support." },
    { title: "Enrichment Activities", description: "Arts, STEM, sports, and guided play built into every afternoon." },
    { title: "Healthy Snacks", description: "Nutritious snacks provided so kids stay fueled until pickup." },
    { title: "Flexible Pickup", description: "School-day and half-day hours designed around working parents." },
  ],
  early_care: [
    { title: "Early Drop-Off", description: "Safe supervised care starting well before the first bell." },
    { title: "Calm Morning Start", description: "Quiet reading, light activities, and breakfast-friendly environment." },
    { title: "On-Campus Convenience", description: "Kids walk straight to class — no second drop-off run." },
    { title: "Trained Staff", description: "Licensed, background-checked team the same faces every morning." },
  ],
  clubs: [
    { title: "Skill Building", description: "Focused instruction in a specific interest, from chess to coding." },
    { title: "Small Group Size", description: "Every child gets attention from an expert instructor." },
    { title: "Season-Long Progress", description: "Clubs run in multi-week sessions so kids visibly improve." },
    { title: "Fun First", description: "Structured but playful — kids leave excited to come back." },
  ],
  summer_camps: [
    { title: "Full-Day Fun", description: "Weekly themed camps that keep summer active, social, and structured." },
    { title: "Weekly Themes", description: "Sports, science, art, coding, adventure — a new focus each week." },
    { title: "Field Trips & Special Guests", description: "Highlights that make the summer memorable." },
    { title: "Sibling Discounts", description: "Family-friendly pricing so brothers and sisters can attend together." },
  ],
  holiday_camps: [
    { title: "School Break Coverage", description: "Full-day care on the days most programs go dark." },
    { title: "Themed Weeks", description: "Winter, spring, and fall break camps with hands-on projects." },
    { title: "Same Trusted Staff", description: "The team your kids already know from after-school." },
    { title: "Flexible Enrollment", description: "Sign up by the day or the week — no long commitment." },
  ],
};

const commonFaqs = [
  {
    question: "How do I register?",
    answer: "Create a parent account, add your child, then choose the program that fits. You can pay per day or set up monthly billing at checkout.",
  },
  {
    question: "What ages do you serve?",
    answer: "Programs are designed for K–8 students, with age-appropriate groupings so kids are always with peers at a similar stage.",
  },
  {
    question: "Are your staff background-checked?",
    answer: "Yes. Every staff member is fingerprint-cleared, CPR/First-Aid certified, and trained on our safety and behavior protocols.",
  },
];

function locPageFor(loc: typeof FREEDOM, neighborhood: string): LandingPageConfig {
  const slug = loc.name.toLowerCase().replace(/\s+/g, "-");
  return {
    slug,
    h1: `Kids Programs at`,
    h1Accent: loc.name,
    seoTitle: `${loc.name} After-School, Clubs & Camps in ${neighborhood}`,
    seoDescription: `On-campus after-school care, clubs, early care, and school-break camps at ${loc.name} in ${neighborhood}. Register your K–8 student today.`,
    heroSubtitle: `Trusted enrichment programs at ${loc.name} — before-school care, after-school care, clubs, and school-break camps, all on the campus your child already knows.`,
    accent: "emerald",
    location: loc,
    neighborhood,
    keywords: [`${loc.name} after school`, `${loc.name} summer camp`, `${neighborhood} kids programs`],
    highlights: programBenefit.after_school,
    faqs: [
      {
        question: `What programs run at ${loc.name}?`,
        answer: `We offer early care, after-school care, seasonal clubs, holiday-break camps, and summer camps directly on the ${loc.name} campus.`,
      },
      ...commonFaqs,
    ],
  };
}

function locProgramPage(
  loc: typeof FREEDOM,
  neighborhood: string,
  program: ProgramKey,
  programLabel: string,
  programSlug: string,
): LandingPageConfig {
  const locSlug = loc.name.toLowerCase().replace(/\s+/g, "-");
  return {
    slug: `${locSlug}/${programSlug}`,
    h1: `${programLabel} at`,
    h1Accent: loc.name,
    seoTitle: `${programLabel} at ${loc.name} — ${neighborhood}`,
    seoDescription: `${programLabel} on the ${loc.name} campus in ${neighborhood}. Safe, staffed, and built for K–8 kids. Enroll online.`,
    heroSubtitle: `Convenient ${programLabel.toLowerCase()} right on the ${loc.name} campus — same trusted staff, no extra drop-off, and easy online enrollment.`,
    accent: "emerald",
    programFilter: program,
    location: loc,
    neighborhood,
    keywords: [
      `${loc.name} ${programLabel.toLowerCase()}`,
      `${programLabel.toLowerCase()} ${neighborhood}`,
      `${programLabel.toLowerCase()} Phoenix AZ`,
    ],
    highlights: programBenefit[program],
    faqs: commonFaqs,
  };
}

function cityProgramPage(
  program: ProgramKey,
  programLabel: string,
  programSlug: string,
  neighborhood: string,
): LandingPageConfig {
  return {
    slug: `${neighborhood.toLowerCase().replace(/\s+/g, "-")}-${programSlug}`,
    h1: `${programLabel} in`,
    h1Accent: neighborhood,
    seoTitle: `${programLabel} in ${neighborhood}, AZ`,
    seoDescription: `${programLabel} for K–8 students in ${neighborhood}, Arizona. Licensed staff, safe campuses, and easy online enrollment.`,
    heroSubtitle: `Local ${programLabel.toLowerCase()} serving families across ${neighborhood} and the surrounding Phoenix area.`,
    accent: "primary",
    programFilter: program,
    neighborhood,
    keywords: [
      `${programLabel.toLowerCase()} ${neighborhood}`,
      `${programLabel.toLowerCase()} Phoenix`,
      `best ${programLabel.toLowerCase()} ${neighborhood}`,
    ],
    highlights: programBenefit[program],
    faqs: commonFaqs,
  };
}

function zipProgramPage(
  program: ProgramKey,
  programLabel: string,
  programSlug: string,
  zip: string,
  areaName: string,
  nearestLoc: typeof FREEDOM,
): LandingPageConfig {
  return {
    slug: `${programSlug}-${zip}`,
    h1: `${programLabel} near`,
    h1Accent: zip,
    seoTitle: `${programLabel} near ${zip} — ${areaName}, Phoenix AZ`,
    seoDescription: `${programLabel} serving families in ${areaName} (${zip}). Convenient to ${nearestLoc.name} — licensed staff, safe campuses, and easy online enrollment.`,
    heroSubtitle: `${programLabel} for K–8 kids in ${areaName} (${zip}), a short drive from our ${nearestLoc.name} campus.`,
    accent: "sky",
    programFilter: program,
    location: nearestLoc,
    neighborhood: areaName,
    keywords: [
      `${programLabel.toLowerCase()} ${zip}`,
      `${programLabel.toLowerCase()} near ${zip}`,
      `${programLabel.toLowerCase()} ${areaName}`,
      `${programLabel.toLowerCase()} Phoenix AZ`,
    ],
    highlights: programBenefit[program],
    faqs: [
      {
        question: `Where is the closest campus to ${zip}?`,
        answer: `Our ${nearestLoc.name} campus at ${nearestLoc.address}, ${nearestLoc.city} is the closest MSB Camps location to families in ${areaName}.`,
      },
      ...commonFaqs,
    ],
  };
}

function neighborhoodHubPage(
  areaName: string,
  slug: string,
  nearestLoc: typeof FREEDOM,
): LandingPageConfig {
  return {
    slug,
    h1: `Kids Programs in`,
    h1Accent: areaName,
    seoTitle: `Kids Programs in ${areaName}, Phoenix — After-School, Clubs & Camps`,
    seoDescription: `After-school care, early care, enrichment clubs, and school-break camps for ${areaName} families. Convenient to our ${nearestLoc.name} campus.`,
    heroSubtitle: `Trusted enrichment programs for ${areaName} families — before-school care, after-school, clubs, holiday camps, and summer camps, hosted at our ${nearestLoc.name} campus nearby.`,
    accent: "violet",
    location: nearestLoc,
    neighborhood: areaName,
    keywords: [
      `${areaName} after school`,
      `${areaName} summer camp`,
      `${areaName} kids programs`,
      `kids camps ${areaName} AZ`,
    ],
    highlights: programBenefit.after_school,
    faqs: [
      {
        question: `Do you serve families in ${areaName}?`,
        answer: `Yes. Many of the families in our programs commute from ${areaName} — our ${nearestLoc.name} campus is a short drive away.`,
      },
      ...commonFaqs,
    ],
  };
}

export const landingPages: LandingPageConfig[] = [
  // Location hubs
  locPageFor(FREEDOM, "North Phoenix"),
  locPageFor(RIDGELINE, "North Phoenix"),

  // Location × program combinations
  locProgramPage(FREEDOM, "North Phoenix", "after_school", "After-School Care", "after-school"),
  locProgramPage(FREEDOM, "North Phoenix", "early_care", "Early Care", "early-care"),
  locProgramPage(FREEDOM, "North Phoenix", "clubs", "Enrichment Clubs", "clubs"),
  locProgramPage(FREEDOM, "North Phoenix", "summer_camps", "Summer Camps", "summer-camps"),
  locProgramPage(FREEDOM, "North Phoenix", "holiday_camps", "Holiday Camps", "holiday-camps"),

  locProgramPage(RIDGELINE, "North Phoenix", "after_school", "After-School Care", "after-school"),
  locProgramPage(RIDGELINE, "North Phoenix", "early_care", "Early Care", "early-care"),
  locProgramPage(RIDGELINE, "North Phoenix", "clubs", "Enrichment Clubs", "clubs"),
  locProgramPage(RIDGELINE, "North Phoenix", "summer_camps", "Summer Camps", "summer-camps"),
  locProgramPage(RIDGELINE, "North Phoenix", "holiday_camps", "Holiday Camps", "holiday-camps"),

  // City / neighborhood program pages
  cityProgramPage("after_school", "After-School Care", "after-school", "North Phoenix"),
  cityProgramPage("summer_camps", "Summer Camps", "summer-camps", "North Phoenix"),
  cityProgramPage("early_care", "Early Care", "early-care", "North Phoenix"),
  cityProgramPage("clubs", "Enrichment Clubs", "clubs", "North Phoenix"),
  cityProgramPage("holiday_camps", "Holiday Camps", "holiday-camps", "North Phoenix"),

  cityProgramPage("after_school", "After-School Care", "after-school", "Paradise Valley"),
  cityProgramPage("summer_camps", "Summer Camps", "summer-camps", "Paradise Valley"),
  cityProgramPage("early_care", "Early Care", "early-care", "Paradise Valley"),

  cityProgramPage("summer_camps", "Summer Camps", "summer-camps", "Phoenix"),
  cityProgramPage("after_school", "After-School Care", "after-school", "Phoenix"),

  // Neighborhood hubs near Freedom Academy (85032)
  neighborhoodHubPage("Paradise Valley", "paradise-valley", FREEDOM),
  neighborhoodHubPage("Desert Ridge", "desert-ridge", FREEDOM),
  neighborhoodHubPage("Scottsdale", "scottsdale", FREEDOM),
  neighborhoodHubPage("Tatum Ranch", "tatum-ranch", FREEDOM),

  // Neighborhood hubs near Ridgeline Academy (85085)
  neighborhoodHubPage("Anthem", "anthem", RIDGELINE),
  neighborhoodHubPage("Deer Valley", "deer-valley", RIDGELINE),
  neighborhoodHubPage("Tramonto", "tramonto", RIDGELINE),
  neighborhoodHubPage("Norterra", "norterra", RIDGELINE),
  neighborhoodHubPage("Cave Creek", "cave-creek", RIDGELINE),

  // Neighborhood × program pages (Freedom Academy area)
  cityProgramPage("after_school", "After-School Care", "after-school", "Desert Ridge"),
  cityProgramPage("summer_camps", "Summer Camps", "summer-camps", "Desert Ridge"),
  cityProgramPage("after_school", "After-School Care", "after-school", "Scottsdale"),
  cityProgramPage("summer_camps", "Summer Camps", "summer-camps", "Scottsdale"),
  cityProgramPage("clubs", "Enrichment Clubs", "clubs", "Scottsdale"),
  cityProgramPage("after_school", "After-School Care", "after-school", "Tatum Ranch"),

  // Neighborhood × program pages (Ridgeline Academy area)
  cityProgramPage("after_school", "After-School Care", "after-school", "Anthem"),
  cityProgramPage("summer_camps", "Summer Camps", "summer-camps", "Anthem"),
  cityProgramPage("holiday_camps", "Holiday Camps", "holiday-camps", "Anthem"),
  cityProgramPage("after_school", "After-School Care", "after-school", "Deer Valley"),
  cityProgramPage("summer_camps", "Summer Camps", "summer-camps", "Deer Valley"),
  cityProgramPage("after_school", "After-School Care", "after-school", "Tramonto"),
  cityProgramPage("after_school", "After-School Care", "after-school", "Norterra"),
  cityProgramPage("summer_camps", "Summer Camps", "summer-camps", "Cave Creek"),
  cityProgramPage("after_school", "After-School Care", "after-school", "Cave Creek"),

  // ZIP-targeted pages (highest-intent local search)
  zipProgramPage("after_school", "After-School Care", "after-school", "85032", "Paradise Valley / North Phoenix", FREEDOM),
  zipProgramPage("summer_camps", "Summer Camps", "summer-camps", "85032", "Paradise Valley / North Phoenix", FREEDOM),
  zipProgramPage("after_school", "After-School Care", "after-school", "85028", "Paradise Valley", FREEDOM),
  zipProgramPage("summer_camps", "Summer Camps", "summer-camps", "85050", "Desert Ridge", FREEDOM),
  zipProgramPage("after_school", "After-School Care", "after-school", "85050", "Desert Ridge", FREEDOM),

  zipProgramPage("after_school", "After-School Care", "after-school", "85085", "North Phoenix / Norterra", RIDGELINE),
  zipProgramPage("summer_camps", "Summer Camps", "summer-camps", "85085", "North Phoenix / Norterra", RIDGELINE),
  zipProgramPage("after_school", "After-School Care", "after-school", "85086", "Anthem / Tramonto", RIDGELINE),
  zipProgramPage("summer_camps", "Summer Camps", "summer-camps", "85086", "Anthem / Tramonto", RIDGELINE),
  zipProgramPage("after_school", "After-School Care", "after-school", "85024", "Deer Valley", RIDGELINE),
  zipProgramPage("summer_camps", "Summer Camps", "summer-camps", "85024", "Deer Valley", RIDGELINE),
];
